/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, READ_RESULT, MODE_NAME, EXIT_FLOW } from './constants';

export default {
  name: 'requirement151',
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      selectedMode: null,
      isDispose: false,
      systemId: '',
      modal_requirement_151: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  watch: {
    // selectedMode の変更を監視
    selectedMode(newVal) {
      this.selectedMode = newVal;
    },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.STOP:
          return 'success';
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 実行中（待機）
      const executionTitle = 'individual.common_title_execution';
      // 確認（工場モード表示, 市場モード表示）
      const confirmationTitle = 'message.common_title_confirmation';
      // 成功（停止）
      const successTitle = 'message.common_title_success';
      // エラー（エラー, 共通エラー）
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.WAIT]: executionTitle,
        [PHASE.FACTORY_MODE_DISPLAY]: confirmationTitle,
        [PHASE.DEALER_MODE_DISPLAY]: confirmationTitle,
        [PHASE.STOP]: successTitle,
        [PHASE.ERROR]: errorTitle,
        [PHASE.FAILED]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        [PHASE.FACTORY_MODE_DISPLAY]: this.createMessageForFactory(),
        [PHASE.DEALER_MODE_DISPLAY]: this.createMessageForDealer(),
        [PHASE.STOP]: `${this.i18nWsKey}.REQ_WS_151_TXT_3`,
        [PHASE.ERROR]: `${this.i18nWsKey}.REQ_WS_151_TXT_4`,
        [PHASE.FAILED]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      return this.phase !== PHASE.WAIT;
    },

    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.FACTORY_MODE_DISPLAY]: true,
        [PHASE.DEALER_MODE_DISPLAY]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    isDisabledOk() {
      let disabledFlag = this.isLoading;
      if ((this.phase === PHASE.FACTORY_MODE_DISPLAY || this.phase === PHASE.DEALER_MODE_DISPLAY) && !this.selectedMode) {
        disabledFlag = true;
      }
      return disabledFlag;
    },

    isDisabledCancel() {
      return this.isLoading;
    },

    /** 工場モード表示で選択できるモード一覧 */
    modeListForFactory() {
      return [
        { value: MODE_NAME.IGON_MODE, text: this.$t(`${this.i18nWsKey}.label_list_item_on_mode`) },
        { value: MODE_NAME.IGOFF_MODE, text: this.$t(`${this.i18nWsKey}.label_list_item_off_mode`) },
        { value: MODE_NAME.RESTRICTION_MODE_END, text: this.$t(`${this.i18nWsKey}.label_list_item_end_mode`) }
      ];
    },

    /** 市場モード表示で選択できるモード一覧 */
    modeListForDealer() {
      return [
        { value: MODE_NAME.IGON_MODE, text: this.$t(`${this.i18nWsKey}.label_list_item_on_mode`) },
        { value: MODE_NAME.IGOFF_MODE, text: this.$t(`${this.i18nWsKey}.label_list_item_off_mode`) },
      ];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_151 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_151 = false;
    },

    /**
     * 工場モード表示用の文言を作成する
     */
    createMessageForFactory() {
      const modeTitle = this.$t(`${this.i18nWsKey}.label_factory_mode_title`);
      const description = this.$t(`${this.i18nWsKey}.label_select_and_press_ok`);
      return `${modeTitle}\n${description}`;
    },

    /**
     * 市場モード表示用の文言を作成する
     */
    createMessageForDealer() {
      const modeTitle = this.$t(`${this.i18nWsKey}.label_dealer_mode_title`);
      const description = this.$t(`${this.i18nWsKey}.label_select_and_press_ok`);
      return `${modeTitle}\n${description}`;
    },

    /** 待機処理 */
    async startProcess() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;
        this.isDispose = response.data.user_data.is_dispose ? response.data.user_data.is_dispose : false;
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            switch (response.data.user_data.result) {
              case READ_RESULT.DEALER:
                // 0x00：市場モード表示へ遷移
                this.phase = PHASE.DEALER_MODE_DISPLAY;
                break;
              case READ_RESULT.FACTORY:
                // 0x01：工場モード表示へ遷移
                this.phase = PHASE.FACTORY_MODE_DISPLAY;
                break;
              case READ_RESULT.OTHER:
                // 上記以外：エラーへ遷移
                await this.errorProcess();
                break;
              default:
                this.showUnexpectedError();
                break;
            }
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            // エラーへ遷移
            await this.errorProcess();
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 市場モード表示のデータ書込み処理 */
    async writeForDealerModeDisplay() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write-dealer'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 各モードのデータ書込み処理へ遷移
            await this.writeForSelectedMode();
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            // エラーへ遷移
            await this.errorProcess();
            break;
          default:
            // 共通エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 各モードのデータ書込み処理 */
    async writeForSelectedMode() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: `write-${this.selectedMode}`
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 停止遷移時の処理へ遷移
            await this.exit(EXIT_FLOW.STOP);
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            // エラーへ遷移
            await this.errorProcess();
            break;
          default:
            // 共通エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** exit処理 */
    async exit(exitFlow) {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        switch (exitFlow) {
          case EXIT_FLOW.STOP:
            this.phase = PHASE.STOP;
            break;
          case EXIT_FLOW.ERROR:
            this.phase = PHASE.ERROR;
            break;
          case EXIT_FLOW.CLOSE:
            this.closeWizard();
            break;
          default:
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        this.phase = PHASE.ERROR;
      } finally {
        this.isLoading = false;
      }
    },

    /** エラー遷移時の処理 */
    async errorProcess() {
      if (this.isDispose) {
        await this.dispose();
      } else {
        await this.exit(EXIT_FLOW.ERROR);
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー表示
      this.phase = PHASE.FAILED;
    },

    /** OKボタン押下時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        // 工場モード表示
        case PHASE.FACTORY_MODE_DISPLAY:
          await this.writeForSelectedMode();
          break;
        // 市場モード表示
        case PHASE.DEALER_MODE_DISPLAY:
          await this.writeForDealerModeDisplay();
          break;
        // 共通エラー  
        case PHASE.FAILED:
          await this.exit(EXIT_FLOW.CLOSE);
          break;
        // 停止  
        // エラー  
        case PHASE.STOP:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** キャンセルボタン押下時の処理 */
    async onCancelClicked() {
      switch (this.phase) {
        // 工場モード表示
        // 市場モード表示  
        case PHASE.FACTORY_MODE_DISPLAY:
        case PHASE.DEALER_MODE_DISPLAY:
          await this.exit(EXIT_FLOW.CLOSE);
          break;
        default:
          break;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  async mounted() {
    this.init();
    await this.startProcess();
  },
};